import { API_BASE_URL } from '../config/apiEndpoints';

/**
 * AI Chat Service – uses backend POST /ai/chat (see AI_CHAT_DECISIONS.md)
 * Backend handles Gemini, venue data, availability, and response formatting.
 */
class AIService {
  constructor() {
    this.backendURL = API_BASE_URL;
    this.conversationHistory = [];
    this.context = {};
  }

  /**
   * No-op: venue data is fetched by the backend. Kept for backward compatibility.
   */
  async initializeVenueData() {
    return null;
  }

  /**
   * Send message to AI via backend POST /ai/chat.
   * Response format per AI_CHAT_DECISIONS.md: success, message, type, data, suggestions, context?, error?
   */
  async sendMessage(message, userContext = {}) {
    try {
      this.context = { ...this.context, ...userContext };

      const headers = {
        'Content-Type': 'application/json'
      };
      const token = localStorage.getItem('access_token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const body = {
        message: message.trim(),
        context: this.context
      };
      if (this.conversationHistory.length > 0) {
        body.conversation_history = this.conversationHistory;
      }

      const url = `${this.backendURL}/ai/chat`;

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      const data = await response.json().catch((e) => {
        return {};
      });

      if (!response.ok) {
        if (response.status === 503) {
          return {
            success: false,
            message: 'AI service is temporarily unavailable. Please try again later.',
            type: 'error',
            error: data.error || 'Service unavailable'
          };
        }
        if (response.status === 404) {
        }
        return {
          success: false,
          message: data.message || 'Something went wrong. Please try again.',
          type: 'error',
          error: data.error || `HTTP ${response.status}`
        };
      }

      if (data.success && data.message) {
        this.conversationHistory.push(
          { role: 'user', content: message },
          { role: 'assistant', content: data.message }
        );
      }

      return {
        success: data.success ?? true,
        message: data.message || '',
        type: data.type || 'text',
        data: data.data,
        suggestions: data.suggestions || [],
        context: data.context ?? this.context,
        error: data.error
      };
    } catch (error) {
      return {
        success: false,
        message: 'I\'m having trouble connecting. Please check your connection and try again.',
        type: 'error',
        error: error.message
      };
    }
  }

  clearHistory() {
    this.conversationHistory = [];
    this.context = {};
  }

  getHistory() {
    return this.conversationHistory;
  }
}

const aiService = new AIService();

export default aiService;
